import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TableKit } from '@tiptap/extension-table';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import styled from 'styled-components';
import { uploadPermanentFile } from '@/services/files';

interface BlogEditorProps {
    value: string;
    onChange: (html: string) => void;
}

// Default author-facing text color in the editor. Set INLINE on the editor
// surface (see editorProps below) so it beats any admin-theme inherited color —
// the cause of the "I can't see what I'm typing" light-gray text. Per-selection
// colors (the color picker) are inline on child <span>s and still override it.
/**
 * The editor surface is deliberately LIGHT even though the admin is dark.
 *
 * This is a WYSIWYG of a blog post, and blog posts render on the marketing
 * site's light background — so dark-on-white here is the post as it will
 * actually look. Darkening this to match the admin chrome would make the
 * preview lie about the thing being previewed, and would leave authors picking
 * text colours against the wrong backdrop. The surrounding chrome (toolbar,
 * borders) is light for the same reason: it frames the paper, not the app.
 */
const EDITOR_TEXT_COLOR = '#1f2430';

const readAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const ToolbarButton = ({
    onClick,
    active,
    disabled,
    title,
    children,
}: {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    title: string;
    children: React.ReactNode;
}) => (
    <button
        type="button"
        title={title}
        aria-label={title}
        onClick={onClick}
        disabled={disabled}
        style={{
            border: '1px solid #d0d0d0',
            background: active ? '#2f80ed' : '#fff',
            color: active ? '#fff' : '#333',
            borderRadius: 4,
            padding: '4px 8px',
            minWidth: 32,
            fontSize: 13,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
        }}
    >
        {/* Emoji glyphs are decorative — the accessible name comes from aria-label. */}
        <span aria-hidden="true">{children}</span>
    </button>
);

// A swatch the author clicks to pick a text/highlight color, with a ✕ to clear
// it. Native <input type="color"> — zero extra deps.
const ColorControl = ({
    title,
    icon,
    value,
    onPick,
    onClear,
}: {
    title: string;
    icon: string;
    value: string;
    onPick: (color: string) => void;
    onClear: () => void;
}) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #d0d0d0', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
        <label title={title} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: 'pointer', padding: '3px 5px' }}>
            <span aria-hidden="true" style={{ fontSize: 13 }}>
                {icon}
            </span>
            <input
                type="color"
                value={value}
                aria-label={title}
                onChange={(e) => onPick(e.target.value)}
                style={{ width: 20, height: 20, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            />
        </label>
        <button
            type="button"
            title={`Clear ${title.toLowerCase()}`}
            aria-label={`Clear ${title.toLowerCase()}`}
            onClick={onClear}
            style={{ border: 'none', borderLeft: '1px solid #eee', background: 'transparent', color: '#888', cursor: 'pointer', padding: '4px 6px', fontSize: 11 }}
        >
            ✕
        </button>
    </span>
);

const Divider = () => <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', background: '#e0e0e0', margin: '0 2px' }} />;

const Toolbar = ({ editor }: { editor: Editor }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-picking the same file
        if (!file) return;
        // Alt text is REQUIRED — it's an SEO + accessibility must-have. Block the
        // insert if the author leaves it empty.
        const alt = window.prompt('Alt text — describe this image for Google Images & screen readers (required):')?.trim();
        if (!alt) {
            window.alert('Image not inserted: alt text is required for SEO/accessibility.');
            return;
        }
        try {
            const base64 = await readAsBase64(file);
            const res = await uploadPermanentFile({ file: base64, path: 'blog/images', name: file.name, contentType: file.type });
            if (res.data?.url) {
                editor.chain().focus().setImage({ src: res.data.url, alt }).run();
            } else {
                window.alert('Image upload failed. Check the console.');
                console.error('Inline image upload failed:', res.error);
            }
        } catch (err) {
            console.error('Inline image upload error:', err);
            window.alert('Image upload failed. Check the console.');
        }
    };

    const setLink = () => {
        const previous = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Link URL (leave empty to remove):', previous ?? 'https://');
        if (url === null) return;
        if (url.trim() === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    };

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, padding: 8, borderBottom: '1px solid #e0e0e0', background: '#fafafa' }}>
            <ToolbarButton title="Heading 2 (section)" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                H2
            </ToolbarButton>
            <ToolbarButton title="Heading 3 (sub-section)" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                H3
            </ToolbarButton>
            <Divider />
            <ToolbarButton title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
                <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
                <em>I</em>
            </ToolbarButton>
            <ToolbarButton title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
                <span style={{ textDecoration: 'underline' }}>U</span>
            </ToolbarButton>
            <ToolbarButton title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
                <span style={{ textDecoration: 'line-through' }}>S</span>
            </ToolbarButton>
            <ColorControl
                title="Text color"
                icon="🎨"
                value={(editor.getAttributes('textStyle').color as string) || EDITOR_TEXT_COLOR}
                onPick={(c) => editor.chain().focus().setColor(c).run()}
                onClear={() => editor.chain().focus().unsetColor().run()}
            />
            <ColorControl
                title="Highlight"
                icon="🖍️"
                value={(editor.getAttributes('highlight').color as string) || '#fff3a3'}
                onPick={(c) => editor.chain().focus().setHighlight({ color: c }).run()}
                onClear={() => editor.chain().focus().unsetHighlight().run()}
            />
            <Divider />
            <ToolbarButton title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
                ⇤
            </ToolbarButton>
            <ToolbarButton title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
                ⇆
            </ToolbarButton>
            <ToolbarButton title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
                ⇥
            </ToolbarButton>
            <Divider />
            <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                • List
            </ToolbarButton>
            <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                1. List
            </ToolbarButton>
            <ToolbarButton title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                ❝
            </ToolbarButton>
            <Divider />
            <ToolbarButton title="Add / edit link" active={editor.isActive('link')} onClick={setLink}>
                🔗
            </ToolbarButton>
            <ToolbarButton title="Remove link" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}>
                ⛓️‍💥
            </ToolbarButton>
            <ToolbarButton title="Insert image (alt text required)" onClick={() => fileInputRef.current?.click()}>
                🖼️
            </ToolbarButton>
            <ToolbarButton title="Insert 3×3 table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
                ▦
            </ToolbarButton>
            <ToolbarButton title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                —
            </ToolbarButton>
            <Divider />
            <ToolbarButton title="Clear formatting (color, highlight, bold, …)" onClick={() => editor.chain().focus().unsetAllMarks().run()}>
                ⌫
            </ToolbarButton>
            <ToolbarButton title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
                ↶
            </ToolbarButton>
            <ToolbarButton title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
                ↷
            </ToolbarButton>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={onPickImage} />
        </div>
    );
};

// Editor surface styling — mirrors the public blog's `.blog-content` semantics
// closely enough that authors author what they'll see (headings start at H2,
// the page owns the single H1).
const EditorShell = styled.div`
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    overflow: hidden;

    .ProseMirror {
        min-height: 320px;
        max-height: 60vh;
        overflow-y: auto;
        padding: 16px 18px;
        outline: none;
        font-size: 15px;
        line-height: 1.7;
        color: ${EDITOR_TEXT_COLOR};
        background: #fff;
    }
    .ProseMirror:focus {
        outline: none;
    }
    .ProseMirror h2 {
        font-size: 1.5em;
        font-weight: 800;
        margin: 1.2em 0 0.5em;
    }
    .ProseMirror h3 {
        font-size: 1.2em;
        font-weight: 700;
        margin: 1em 0 0.4em;
    }
    .ProseMirror p {
        margin: 0 0 0.9em;
    }
    .ProseMirror ul,
    .ProseMirror ol {
        padding-left: 1.4em;
        margin: 0 0 0.9em;
    }
    .ProseMirror blockquote {
        border-left: 3px solid #2f80ed;
        margin: 0 0 0.9em;
        padding-left: 1em;
        color: #555;
    }
    .ProseMirror a {
        color: #2f80ed;
        text-decoration: underline;
    }
    /* Highlight (mark) — readable in the light editor; the site re-styles it for
       its dark theme. A per-selection text color (the picker) still wins. */
    .ProseMirror mark {
        border-radius: 3px;
        padding: 0.05em 0.2em;
    }
    .ProseMirror img {
        max-width: 100%;
        height: auto;
        border-radius: 6px;
    }
    .ProseMirror table {
        border-collapse: collapse;
        width: 100%;
        margin: 0 0 0.9em;
    }
    .ProseMirror th,
    .ProseMirror td {
        border: 1px solid #d0d0d0;
        padding: 6px 10px;
        text-align: left;
    }
    .ProseMirror th {
        background: #f4f6fb;
        font-weight: 700;
    }
    .ProseMirror p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: #9aa0a6;
        pointer-events: none;
        height: 0;
    }
`;

const BlogEditor = ({ value, onChange }: BlogEditorProps) => {
    const editor = useEditor({
        extensions: [
            // StarterKit (v3) bundles Bold/Italic/Underline/Strike/Lists/Blockquote/
            // HR/History/Link. Headings constrained to H2/H3 — the post title is the
            // page's single H1, so authors must never emit an H1 in the body.
            StarterKit.configure({
                heading: { levels: [2, 3] },
                link: {
                    openOnClick: false,
                    autolink: true,
                    HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
                },
            }),
            // TextStyle is the carrier mark Color writes onto (<span style="color">).
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Image.configure({ HTMLAttributes: { loading: 'lazy' } }),
            Placeholder.configure({ placeholder: 'Write the post… Use H2 for sections, H3 for sub-sections. Every image needs alt text.' }),
            TableKit.configure({ table: { resizable: false } }),
        ],
        // Inline style on the contenteditable element beats any inherited
        // admin-theme color, so authors always see dark, readable text (fixes the
        // light-gray-on-white complaint). Per-selection colors override per span.
        editorProps: { attributes: { style: `color: ${EDITOR_TEXT_COLOR};` } },
        content: value || '',
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
    });

    // Load async-fetched content (editing an existing post) without clobbering
    // the author mid-type: only re-sync when the incoming value differs and the
    // editor isn't focused.
    useEffect(() => {
        if (!editor) return;
        const incoming = value || '';
        if (incoming !== editor.getHTML() && !editor.isFocused) {
            editor.commands.setContent(incoming, { emitUpdate: false });
        }
    }, [value, editor]);

    if (!editor) return null;

    return (
        <EditorShell>
            <Toolbar editor={editor} />
            <EditorContent editor={editor} />
        </EditorShell>
    );
};

export default BlogEditor;
